import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Text,
    Center,
    Loader,
    Stack,
    Flex,
    TextInput,
    Button,
    Title,
    Pagination,
} from "@mantine/core";
import ShiftSignupCard from "./ShiftSignupCard";
import { Shift } from "../types/Shift";
import { IconSearch, IconSortAscending, IconSortDescending } from "@tabler/icons-react";
import { getAuthHeaders } from "../api/authHelper";
interface Props {
    eventId: number;
    language: string;
    onRegistrationChanged?: () => void;
}

const AllAvailableShiftList: React.FC<Props> = ({ eventId, language, onRegistrationChanged }) => {
    const [shifts, setShifts] = useState<Shift[]>([]);
    const [loading, setLoading] = useState(true);
    const [showOnlyAvailable, setShowOnlyAvailable] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [sortAsc, setSortAsc] = useState(true);
    const [activePage, setActivePage] = useState(1);

    const navigate = useNavigate();

    const personId = Number(sessionStorage.getItem("personId")) || 0;

    const [registeredShiftIds, setRegisteredShiftIds] = useState<number[]>([]);
    const registeredSet = useMemo(() => new Set(registeredShiftIds), [registeredShiftIds]);

    const ITEMS_PER_PAGE = 25;

    useEffect(() => {
        const fetchAll = async () => {
            try {
                const apiUrl = process.env.REACT_APP_API_URL;

                const shiftsRes = await fetch(`${apiUrl}/api/events/${eventId}/shifts/available`);
                const shiftsData = await shiftsRes.json();
                setShifts(shiftsData);

                const regRes = await fetch(
                    `${apiUrl}/api/shifts/registrations?personId=${personId}`,
                    {
                        headers: getAuthHeaders(),
                    }
                );
                const regIds = await regRes.json();
                setRegisteredShiftIds(regIds);
            } catch (error) {
                console.error("Failed to fetch shifts/registrations:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchAll();
    }, [eventId, personId]);

    // Reset til side 1 når der søges/sorteres/filtreres
    useEffect(() => {
        setActivePage(1);
    }, [searchTerm, sortAsc, showOnlyAvailable]);

    if (loading) {
        return (
            <Center mt="md">
                <Loader color="red" />
            </Center>
        );
    }

    const handleRegistrationChangedAndRefresh = (shiftsId: number, isNowRegistered: boolean) => {
        setShifts((prev) =>
            prev.map((s) => {
                if (s.shiftsID !== shiftsId) return s;

                const nextSpots = isNowRegistered
                    ? Math.max(0, (s.availableSpots ?? 0) - 1)
                    : (s.availableSpots ?? 0) + 1;

                return { ...s, availableSpots: nextSpots };
            })
        );

        setRegisteredShiftIds((prev) => {
            const set = new Set(prev);
            if (isNowRegistered) set.add(shiftsId);
            else set.delete(shiftsId);
            return Array.from(set);
        });

        onRegistrationChanged?.();
    };

    const filteredShifts = shifts
        .filter((s) => {
            const term = searchTerm.trim().toLowerCase();

            const startDate = new Date(s.startTime);

            const nameStr = (s.shiftsName ?? "").toLowerCase();
            const functionStr = (s.functionName ?? "").toLowerCase();

            const dayStr = startDate
                .toLocaleDateString("da-DK", { weekday: "long" })
                .toLowerCase();

            const dateStr = startDate.toLocaleDateString("da-DK"); // fx 4.5.2026
            const dateStrPadded = startDate.toLocaleDateString("da-DK", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
            }); // fx 04.05.2026

            const dayOnly = startDate.toLocaleDateString("da-DK", {
                day: "2-digit",
            }); // fx 04

            const monthOnly = startDate.toLocaleDateString("da-DK", {
                month: "2-digit",
            }); // fx 05

            const normalizedTerm = term.replace(/\s/g, "").replace(/-/g, ".").replace(/\//g, ".");
            const normalizedDate = dateStr.replace(/\s/g, "");
            const normalizedDatePadded = dateStrPadded.replace(/\s/g, "");

            const matchesSearch =
                term === "" ||
                nameStr.includes(term) ||
                functionStr.includes(term) ||
                dayStr.includes(term) ||
                normalizedDate.includes(normalizedTerm) ||
                normalizedDatePadded.includes(normalizedTerm) ||
                dayOnly.includes(normalizedTerm) ||
                monthOnly.includes(normalizedTerm);

            const matchesAvailability = !showOnlyAvailable || (s.availableSpots ?? 0) > 0;

            return matchesAvailability && matchesSearch;
        })
        .sort((a, b) => {
            const dateA = new Date(a.startTime).getTime();
            const dateB = new Date(b.startTime).getTime();
            return sortAsc ? dateA - dateB : dateB - dateA;
        });

    const totalPages = Math.ceil(filteredShifts.length / ITEMS_PER_PAGE);

    const paginatedShifts = filteredShifts.slice(
        (activePage - 1) * ITEMS_PER_PAGE,
        activePage * ITEMS_PER_PAGE
    );

    return (
        <div style={{ width: "60%", margin: "0 auto" }}>
            <Stack>
                <Center my="md" style={{ flexDirection: "column", gap: 8 }}>
                    <Title order={2}>
                        {language === "da" ? "Alle ledige vagter" : "All available shifts"}
                    </Title>

                    <Button
                        variant="light"
                        size="xs"
                        color="gray"
                        onClick={() => navigate(`/EventPage/${eventId}`)}
                    >
                        {language === "da"
                            ? "← Tilbage til funktioner"
                            : "← Back to functions"}
                    </Button>
                </Center>
                <Flex gap="sm" justify="center" align="flex-end" wrap="wrap" mt="md">
                    <TextInput
                        style={{ flex: 1, minWidth: "240px", maxWidth: "400px" }}
                        label={language === "da" ? "Søg vagt eller dato" : "Search shift or date"}
                        placeholder={language === "da" ? "f.eks. Fredag eller 17.04" : "e.g. Friday or 17.04"}
                        value={searchTerm}
                        onChange={(event) => setSearchTerm(event.currentTarget.value)}
                        leftSection={<IconSearch size={16} />}
                    />

                    <Button
                        variant="light"
                        onClick={() => setSortAsc(!sortAsc)}
                        leftSection={sortAsc ? <IconSortAscending size={16} /> : <IconSortDescending size={16} />}
                        style={{ height: "40px" }}
                    >
                        {language === "da" ? "Sorter dato" : "Sort date"}
                    </Button>
                </Flex>
                {filteredShifts.length === 0 ? (
                    <Text>{language === "da" ? "Ingen vagter fundet." : "No shifts found."}</Text>
                ) : (
                    <>
                        <Title order={4}>{language === "da" ? "Vagter" : "Shifts"}</Title>
                        <Text size="sm" c="dimmed">
                            {language === "da"
                                ? `Viser ${(activePage - 1) * ITEMS_PER_PAGE + 1}-${Math.min(
                                    activePage * ITEMS_PER_PAGE,
                                    filteredShifts.length
                                )} af ${filteredShifts.length} vagter`
                                : `Showing ${(activePage - 1) * ITEMS_PER_PAGE + 1}-${Math.min(
                                    activePage * ITEMS_PER_PAGE,
                                    filteredShifts.length
                                )} of ${filteredShifts.length} shifts`}
                        </Text>

                        <Stack gap="sm">
                            {paginatedShifts.map((shift) => (
                                <ShiftSignupCard
                                    key={shift.shiftsID}
                                    shiftsID={shift.shiftsID}
                                    shiftsName={shift.shiftsName}
                                    startTime={shift.startTime}
                                    endTime={shift.endTime}
                                    endsOneHourAfterPlay={shift.endsOneHourAfterPlay}
                                    availableSpots={shift.availableSpots}
                                    functionName={shift.functionName}
                                    functionId={shift.functionId}
                                    eventId={eventId}
                                    language={language}
                                    personId={personId}
                                    isInitiallyRegistered={registeredSet.has(shift.shiftsID)}
                                    allowWaitlist={true}
                                    onRegistrationChanged={(id, state) => {
                                        handleRegistrationChangedAndRefresh(id, state === "confirmed");
                                    }}
                                />
                            ))}
                        </Stack>
                        {totalPages > 1 && (
                            <Center mt="md">
                                <Pagination
                                    value={activePage}
                                    onChange={setActivePage}
                                    total={totalPages}
                                />
                            </Center>
                        )}
                    </>
                )}
            </Stack>
        </div>
    );
};

export default AllAvailableShiftList;